import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FileText, Loader2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_STYLE = { open: 'text-amber-400 border-amber-500/40', paid: 'text-emerald-400 border-emerald-500/40', void: 'text-muted-foreground border-border' };

export const PlatformInvoices = () => {
  const [invoices, setInvoices] = useState(null);
  const [form, setForm] = useState({ email: '', amount: '', description: '', due_date: '' });
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/platform-invoices`, auth())
      .then(r => setInvoices(r.data.invoices))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load invoices'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const cents = Math.round(parseFloat(form.amount) * 100);
    if (!form.email || !cents || cents < 100 || !form.description) return toast.error('Email, amount (min $1) and description are required');
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/platform-invoices`, {
        email: form.email, amount_cents: cents, description: form.description, due_date: form.due_date || null,
      }, auth());
      toast.success(`Invoice ${r.data.number} sent to ${r.data.to_name}`);
      setForm({ email: '', amount: '', description: '', due_date: '' });
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not create invoice');
    } finally { setSending(false); }
  };

  const voidInv = async (inv) => {
    if (!window.confirm(`Void invoice ${inv.number} (${usd(inv.amount_cents)} to ${inv.to_name})?`)) return;
    try {
      await axios.post(`${API}/admin/platform-invoices/${inv.invoice_id}/void`, {}, auth());
      toast.success(`${inv.number} voided`); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not void'); }
  };

  return (
    <div data-testid="platform-invoices-section">
      <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><FileText className="h-4 w-4 text-amber-400" /> Platform Invoices — bill any user, paid from their Eventism wallet</p>
      <Card className="border-border mb-3"><CardContent className="py-3">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <Input placeholder="User email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 text-sm lg:col-span-1" data-testid="invoice-email-input" />
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
            <Input placeholder="0.00" inputMode="decimal" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="h-9 pl-6 text-sm" data-testid="invoice-amount-input" />
          </div>
          <Input placeholder="What's this for? (e.g. Treasury loan repayment)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-9 text-sm lg:col-span-2" data-testid="invoice-desc-input" />
          <div className="flex gap-2">
            <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="h-9 text-sm" data-testid="invoice-due-input" />
            <Button size="sm" className="h-9 shrink-0" onClick={create} disabled={sending} data-testid="create-invoice-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Invoice</>}
            </Button>
          </div>
        </div>
      </CardContent></Card>

      <div className="rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead><tr className="bg-muted/40 text-left"><th className="px-3 py-2">#</th><th className="px-3 py-2">Billed to</th><th className="px-3 py-2">For</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr></thead>
          <tbody>
            {!invoices ? <tr><td colSpan={6} className="px-3 py-5 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></td></tr>
              : invoices.length === 0 ? <tr><td colSpan={6} className="px-3 py-5 text-center text-xs text-muted-foreground">No invoices yet.</td></tr>
              : invoices.map(inv => (
                <tr key={inv.invoice_id} className="border-t border-border/60">
                  <td className="px-3 py-1.5 font-mono text-xs">{inv.number}</td>
                  <td className="px-3 py-1.5 text-xs">{inv.to_name}<span className="text-muted-foreground block">{inv.to_email}</span></td>
                  <td className="px-3 py-1.5 text-xs max-w-[220px] truncate">{inv.description}</td>
                  <td className="px-3 py-1.5 text-right font-semibold">{usd(inv.amount_cents)}</td>
                  <td className="px-3 py-1.5"><span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLE[inv.status] || ''}`}>{inv.status}</span>
                    {inv.paid_at && <span className="block text-[10px] text-muted-foreground">{new Date(inv.paid_at).toLocaleDateString()}</span>}</td>
                  <td className="px-3 py-1.5 text-right">
                    {inv.status === 'open' && <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-400" onClick={() => voidInv(inv)} data-testid={`void-invoice-${inv.number}`}>Void</Button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
